import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Modal from '../../../components/orizon-ui/Modal';
import QtyStepper from '../../../components/orizon-ui/QtyStepper';
import { FormSection, ToggleField, SelectField, InfoBox, FieldShell } from '../../../components/orizon-ui/FormField';
import { useToast } from '../../../components/orizon-ui/Toast';
import { useBarcodeCodice } from '../../../components/colata/BarcodeCodice';
import { FORMATI_CARTELLINO, formatoCartellino, stampaCartellini } from '../../../components/colata/CartellinoStampa';
import { CartellinoV2Anteprima, AreaStampaV2 } from '../../../components/colata/CartellinoDesign';
import {
  useImpostazioniCartellino, ALLINEAMENTI, SPAZIATURE, ALTEZZE_BARCODE, SPESSORI_CORNICE, COLORI, FONT_PX,
} from '../../../components/colata/impostazioniCartellino';
import { formattaQta } from '../../../components/gru/scorta';
import { gruLista, mapGruError } from '../../../lib/services/gruService';

const MAX_COPIE = 200;

/** Gruppo di pulsanti a scelta singola. opzioni: [{ id, label, icona? }] */
const Segmentato = ({ etichetta, valore, opzioni, onChange, disabilitato = false }) => (
  <FieldShell label={etichetta} name={etichetta}>
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={etichetta}>
      {opzioni.map((o) => {
        const scelto = o.id === valore;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={scelto}
            disabled={disabilitato}
            onClick={() => onChange(o.id)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-[11px] font-black uppercase tracking-widest transition-all disabled:opacity-40 ${
              scelto
                ? 'bg-primary/10 border-primary/40 text-primary'
                : 'bg-muted/30 border-border/40 text-muted-foreground hover:border-primary/30 hover:text-foreground'
            }`}
          >
            {o.icona && <Icon name={o.icona} size={13} />}
            {o.label}
          </button>
        );
      })}
    </div>
  </FieldShell>
);

/**
 * Modale "Cartellino" di GRU (stesso design v2 di Colata): cornice, filetti grigi, scorta minima in basso a destra.
 * Tutte le impostazioni (formato, contenuto, corpo in px, allineamento, spaziatura, colore ...)
 * si salvano nel browser e si ritrovano alla prossima apertura.
 */
const CartellinoModal = ({ articolo, onClose }) => {
  const toast = useToast();
  const { svg, errore, caricamento } = useBarcodeCodice(articolo.ean);
  const [imp, imposta, ripristina] = useImpostazioniCartellino('gru');
  const formato = formatoCartellino(imp.formato);

  const [copie, setCopie] = useState(1);
  const [etichette, setEtichette] = useState([]); // etichette dei componenti collegati
  const [etichettaScelta, setEtichettaScelta] = useState('');
  const [scala] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 560 ? 1 : 1.2));

  useEffect(() => {
    let annullato = false;
    gruLista('compatibilita', { perPagina: 50, filtri: { articolo_id: articolo.id } })
      .then((r) => {
        if (annullato) return;
        const uniche = [...new Set(r.righe.map((k) => (k.componente_etichetta || '').trim()).filter(Boolean))];
        setEtichette(uniche);
        setEtichettaScelta(uniche[0] ?? '');
      })
      .catch((e) => {
        if (!annullato) toast.error(mapGruError(e).message);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articolo.id]);

  const ubicazione = articolo.ubicazione_codice
    ? `${articolo.magazzino_label ? `${articolo.magazzino_label} · ` : ''}${articolo.ubicazione_codice}`
    : null;
  const testoScorta = `${formattaQta(articolo.qta_min, articolo.unita_decimali)} ${articolo.unita_label}`;

  const dati = useMemo(() => {
    const voci = [];
    if (imp.mostraSap && articolo.codice_sap) voci.push(`SAP ${articolo.codice_sap}`);
    if (imp.mostraUbicazione && ubicazione) voci.push(`UBIC. ${ubicazione}`);
    return {
      nome: imp.mostraNome ? articolo.nome : null,
      etichetta: imp.mostraEtichetta && etichettaScelta ? etichettaScelta : null,
      voci,
      scortaMin: imp.mostraScortaMin ? `SCORTA MIN: ${testoScorta}` : null,
    };
  }, [articolo, ubicazione, testoScorta, imp, etichettaScelta]);

  const puoStampare = Boolean(svg) && !errore && !caricamento;
  const cambiaPx = (d) => imposta('fontPx', Math.min(FONT_PX.max, Math.max(FONT_PX.min, imp.fontPx + d)));

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        titolo={articolo.nome}
        etichetta={`Cartellino · ${articolo.ean || '—'}`}
        larghezza="lg"
        footer={(
          <>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-muted text-foreground rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-muted/80 transition-all border border-border/40"
            >
              Chiudi
            </button>
            <button
              type="button"
              onClick={stampaCartellini}
              disabled={!puoStampare}
              className="flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-2xl text-[11px] font-black uppercase tracking-[.2em] hover:opacity-90 transition-all shadow-lg shadow-primary/20 disabled:opacity-50"
            >
              <Icon name="Printer" size={14} />
              Stampa{copie > 1 ? ` (${copie})` : ''}
            </button>
          </>
        )}
      >
        {errore && <InfoBox tono="warn">{errore}</InfoBox>}

        {/* Anteprima */}
        <FormSection titolo={`Anteprima ${formato.label}`} icona="Barcode">
          <div className="flex justify-center p-4 md:p-6 rounded-2xl bg-muted/30 border border-border/30 overflow-auto">
            {caricamento ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground/50">
                <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                <span className="text-[10px] font-black uppercase tracking-widest">Generazione codice a barre...</span>
              </div>
            ) : (
              <CartellinoV2Anteprima dati={dati} svg={svg} imp={imp} scala={scala} />
            )}
          </div>
        </FormSection>

        {/* Formato */}
        <FormSection titolo="Formato" icona="Ruler">
          <Segmentato
            etichetta="Dimensione del cartellino"
            valore={imp.formato}
            opzioni={FORMATI_CARTELLINO.map((f) => ({ id: f.id, label: `${f.label} · ${f.nota}` }))}
            onChange={(v) => imposta('formato', v)}
          />
        </FormSection>

        {/* Contenuto */}
        <FormSection titolo="Contenuto" icona="ListChecks">
          <ToggleField label="Nome articolo" sublabel={articolo.nome} checked={imp.mostraNome} onChange={(v) => imposta('mostraNome', v)} />
          <ToggleField
            label="Scorta minima"
            sublabel={`in basso a destra · ${testoScorta}`}
            checked={imp.mostraScortaMin}
            onChange={(v) => imposta('mostraScortaMin', v)}
          />
          <ToggleField
            label="Codice SAP"
            sublabel={articolo.codice_sap ? articolo.codice_sap : 'non presente nell\'articolo'}
            checked={imp.mostraSap}
            onChange={(v) => imposta('mostraSap', v)}
          />
          <ToggleField
            label="Ubicazione"
            sublabel={ubicazione || 'non presente nell\'articolo'}
            checked={imp.mostraUbicazione}
            onChange={(v) => imposta('mostraUbicazione', v)}
          />
          <ToggleField
            label="Etichetta del componente"
            sublabel={etichette.length ? (etichettaScelta || undefined) : 'nessun componente collegato con etichetta'}
            checked={imp.mostraEtichetta}
            onChange={(v) => imposta('mostraEtichetta', v)}
            disabilitato={etichette.length === 0}
          />
          {imp.mostraEtichetta && etichette.length > 1 && (
            <SelectField
              label="Quale etichetta" name="etichetta" value={etichettaScelta}
              onChange={setEtichettaScelta}
              opzioni={etichette.map((e) => ({ value: e, label: e }))}
              segnaposto="— Seleziona —"
            />
          )}
        </FormSection>

        {/* Aspetto */}
        <FormSection titolo="Aspetto" icona="SlidersHorizontal">
          <FieldShell
            label="Dimensione del testo"
            name="fontPx"
            suggerimento={imp.adatta
              ? 'Corpo massimo: se il testo è lungo si rimpicciolisce da solo per stare nel cartellino.'
              : 'Corpo fisso: se il testo è troppo lungo viene tagliato.'}
          >
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => cambiaPx(-1)} disabled={imp.fontPx <= FONT_PX.min} aria-label="Riduci il testo"
                className="w-10 h-10 rounded-xl bg-muted/40 border border-border/40 hover:bg-primary hover:text-primary-foreground hover:border-primary text-lg font-black disabled:opacity-40 transition-all">−</button>
              <span className="min-w-[72px] text-center text-xl font-black tabular-nums">{imp.fontPx}<span className="ml-1 text-[10px] font-black uppercase tracking-widest text-muted-foreground/60">px</span></span>
              <button type="button" onClick={() => cambiaPx(1)} disabled={imp.fontPx >= FONT_PX.max} aria-label="Ingrandisci il testo"
                className="w-10 h-10 rounded-xl bg-muted/40 border border-border/40 hover:bg-primary hover:text-primary-foreground hover:border-primary text-lg font-black disabled:opacity-40 transition-all">+</button>
            </div>
          </FieldShell>
          <ToggleField
            label="Adatta automaticamente"
            sublabel="rimpicciolisce il testo lungo"
            checked={imp.adatta}
            onChange={(v) => imposta('adatta', v)}
          />
          <Segmentato etichetta="Allineamento del testo" valore={imp.allineamento} opzioni={ALLINEAMENTI} onChange={(v) => imposta('allineamento', v)} />
          <Segmentato etichetta="Spazio tra le righe" valore={imp.spaziatura} opzioni={SPAZIATURE} onChange={(v) => imposta('spaziatura', v)} />
          <Segmentato etichetta="Altezza del codice a barre" valore={imp.altezzaBarcode} opzioni={ALTEZZE_BARCODE} onChange={(v) => imposta('altezzaBarcode', v)} />
          <ToggleField label="Cornice" sublabel="linea intorno al cartellino" checked={imp.cornice} onChange={(v) => imposta('cornice', v)} />
          {imp.cornice && (
            <Segmentato etichetta="Spessore della cornice" valore={imp.spessoreCornice} opzioni={SPESSORI_CORNICE} onChange={(v) => imposta('spessoreCornice', v)} />
          )}
          <ToggleField label="Linee di separazione" sublabel="grigio chiaro, sotto il codice e sopra la scorta" checked={imp.divisori} onChange={(v) => imposta('divisori', v)} />
          <ToggleField label="Testo maiuscolo" checked={imp.maiuscolo} onChange={(v) => imposta('maiuscolo', v)} />
          <FieldShell label="Colore di testo e cornice" name="colore" suggerimento="Il codice a barre resta sempre nero, per essere letto dallo scanner.">
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Colore">
              {COLORI.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={imp.colore === c.id}
                  onClick={() => imposta('colore', c.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-[11px] font-black uppercase tracking-widest transition-all ${
                    imp.colore === c.id ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-muted/30 border-border/40 text-muted-foreground hover:border-primary/30'
                  }`}
                >
                  <span className="w-4 h-4 rounded-full border border-border/60" style={{ background: c.hex }} />
                  {c.label}
                </button>
              ))}
            </div>
          </FieldShell>
          <div className="flex items-center justify-between gap-3 pt-1">
            <span className="text-[10px] font-bold text-muted-foreground/50">Le impostazioni si salvano automaticamente in questo browser.</span>
            <button
              type="button"
              onClick={() => { ripristina(); toast.success('Impostazioni del cartellino ripristinate.'); }}
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-muted/40 border border-border/40 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all shrink-0"
            >
              <Icon name="RotateCcw" size={13} />
              Ripristina
            </button>
          </div>
        </FormSection>

        {/* Copie */}
        <FormSection titolo="Numero di copie" icona="Copy">
          <QtyStepper
            nome="copie"
            value={copie}
            onChange={(v) => setCopie(Math.min(MAX_COPIE, Math.max(1, Math.round(v) || 1)))}
            min={1}
            max={MAX_COPIE}
            decimali={0}
            unitaLabel={copie === 1 ? 'cartellino' : 'cartellini'}
          />
          <InfoBox>
            Per stampare: nella finestra di stampa scegli la stampante di etichette, formato carta personalizzato{' '}
            {formato.label} ({formato.larghezza} di larghezza × {formato.altezza} di altezza), margini "Nessuno" e scala 100%.
            Esce un cartellino per pagina.
          </InfoBox>
        </FormSection>
      </Modal>

      {/* Pagine di stampa (nascoste sullo schermo) */}
      {puoStampare && <AreaStampaV2 dati={dati} svg={svg} imp={imp} copie={copie} />}
    </>
  );
};

export default CartellinoModal;
